import { useState } from "react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Filter, Download, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USAGE_TYPES = [
  "Bireysel Kullanıcı",
  "İşveren / İşyeri",
  "Belediye / Kamu Kurumu",
  "Sağlık / Eğitim Kurumu",
  "Eczane / Medikal Firma",
  "Diğer",
];

const BAC_BANDS = [
  { label: "0 - 0.39‰", value: "0-0.39" },
  { label: "0.40 - 0.49‰", value: "0.40-0.49" },
  { label: "0.50+‰", value: "0.50+" },
];

const CHANNELS = [
  { label: "Web", value: "web" },
  { label: "Mobil", value: "mobile" },
];

const AdminLeadTable = ({ leads, onFilterChange, onRefresh }) => {
  const [usageType, setUsageType] = useState("all");
  const [bacBand, setBacBand] = useState("all");
  const [channel, setChannel] = useState("all");
  const [exportingCSV, setExportingCSV] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [deletingBulk, setDeletingBulk] = useState(false);

  const handleApplyFilters = () => {
    const filters = {};
    if (usageType && usageType !== "all") filters.usage_type = usageType;
    if (bacBand && bacBand !== "all") filters.bac_band = bacBand;
    if (channel && channel !== "all") filters.channel = channel;
    onFilterChange(filters);
  };

  const handleResetFilters = () => {
    setUsageType("all");
    setBacBand("all");
    setChannel("all");
    onFilterChange({});
    onRefresh();
  };

  const handleExportCSV = async () => {
    const token = localStorage.getItem("admin_token");
    setExportingCSV(true);

    try {
      const params = {};
      if (usageType && usageType !== "all") params.usage_type = usageType;
      if (bacBand && bacBand !== "all") params.bac_band = bacBand;
      if (channel && channel !== "all") params.channel = channel;

      const response = await axios.get(`${API}/admin/leads/export-csv`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `alcodot_leads_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("CSV dosyası indirildi!");
    } catch (error) {
      console.error("CSV export error:", error);
      toast.error("CSV indirme başarısız");
    } finally {
      setExportingCSV(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Bu lead'i silmek istediğinizden emin misiniz?")) {
      return;
    }

    const token = localStorage.getItem("admin_token");
    setDeletingId(leadId);

    try {
      await axios.delete(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Lead silindi!");
      onRefresh();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Silme başarısız");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(leads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    if (selectedLeads.includes(leadId)) {
      setSelectedLeads(selectedLeads.filter(id => id !== leadId));
    } else {
      setSelectedLeads([...selectedLeads, leadId]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Lütfen silinecek lead'leri seçin");
      return;
    }

    if (!window.confirm(`${selectedLeads.length} lead'i silmek istediğinizden emin misiniz?`)) {
      return;
    }

    const token = localStorage.getItem("admin_token");
    setDeletingBulk(true);

    try {
      await axios.post(
        `${API}/admin/leads/bulk-delete`,
        { lead_ids: selectedLeads },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${selectedLeads.length} lead silindi!`);
      setSelectedLeads([]);
      onRefresh();
    } catch (error) {
      console.error("Bulk delete error:", error);
      toast.error("Toplu silme başarısız");
    } finally {
      setDeletingBulk(false);
    }
  };

  const getBacColor = (bac) => {
    if (bac < 0.4) return "text-green-400 bg-green-500/20 border border-green-500/30";
    if (bac < 0.5) return "text-yellow-400 bg-yellow-500/20 border border-yellow-500/30";
    return "text-red-400 bg-red-500/20 border border-red-500/30";
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-lead-table">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black text-gradient">Lead Listesi</h2>
        <div className="flex gap-3">
          {selectedLeads.length > 0 && (
            <Button
              onClick={handleBulkDelete}
              disabled={deletingBulk}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 rounded-xl bg-red-500/10 border-2 border-red-500/30 text-red-400 hover:border-red-500 hover:bg-red-500/20 font-bold"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-5 h-5" />
              {deletingBulk ? "Siliniyor..." : `Seçilenleri Sil (${selectedLeads.length})`}
            </Button>
          )}
          <Button
            onClick={handleExportCSV}
            disabled={exportingCSV}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 rounded-xl bg-green-500/10 border-2 border-green-500/30 text-green-400 hover:border-green-500 hover:bg-green-500/20 font-bold"
            data-testid="export-csv-btn"
          >
            <Download className="w-5 h-5" />
            {exportingCSV ? "İndiriliyor..." : "CSV İndir"}
          </Button>
          <Button
            onClick={onRefresh}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 rounded-xl bg-teal-500/10 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500 hover:bg-teal-500/20 font-bold"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className="w-5 h-5" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card-premium rounded-2xl p-6 neon-border">
        <div className="flex items-center gap-3 mb-5">
          <Filter className="w-5 h-5 text-teal-400" />
          <h3 className="text-lg font-black text-teal-400 uppercase">Filtreler</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div>
            <label className="text-xs text-teal-400 mb-2 block font-bold uppercase tracking-wider">Kullanım Tipi</label>
            <Select value={usageType} onValueChange={setUsageType}>
              <SelectTrigger className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white" data-testid="filter-usage-type">
                <SelectValue placeholder="Tümü" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-teal-500/30">
                <SelectItem value="all" className="text-white hover:bg-teal-500/20">Tümü</SelectItem>
                {USAGE_TYPES.map((type) => (
                  <SelectItem key={type} value={type} className="text-white hover:bg-teal-500/20">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-teal-400 mb-2 block font-bold uppercase tracking-wider">Promil Bandı</label>
            <Select value={bacBand} onValueChange={setBacBand}>
              <SelectTrigger className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white" data-testid="filter-bac-band">
                <SelectValue placeholder="Tümü" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-teal-500/30">
                <SelectItem value="all" className="text-white hover:bg-teal-500/20">Tümü</SelectItem>
                {BAC_BANDS.map((band) => (
                  <SelectItem key={band.value} value={band.value} className="text-white hover:bg-teal-500/20">
                    {band.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs text-teal-400 mb-2 block font-bold uppercase tracking-wider">Kanal</label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="h-12 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-white" data-testid="filter-channel">
                <SelectValue placeholder="Tümü" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-teal-500/30">
                <SelectItem value="all" className="text-white hover:bg-teal-500/20">Tümü</SelectItem>
                {CHANNELS.map((ch) => (
                  <SelectItem key={ch.value} value={ch.value} className="text-white hover:bg-teal-500/20">
                    {ch.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handleApplyFilters}
            size="sm"
            className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black rounded-xl font-bold px-6"
            data-testid="apply-filters-btn"
          >
            Filtrele
          </Button>
          <Button
            onClick={handleResetFilters}
            size="sm"
            variant="outline"
            className="rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500 hover:bg-teal-500/10 font-bold px-6"
            data-testid="reset-filters-btn"
          >
            Temizle
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card-premium rounded-2xl overflow-hidden neon-border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50 border-b-2 border-teal-500/30">
              <tr>
                <th className="px-5 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={selectedLeads.length === leads.length && leads.length > 0}
                    onChange={handleSelectAll}
                    className="w-5 h-5 rounded border-teal-500/50 bg-gray-900/50 checked:bg-teal-500 cursor-pointer"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-5 py-4 text-left text-xs font-black text-teal-400 uppercase tracking-wider">
                  Ad Soyad
                </th>
                <th className="px-5 py-4 text-left text-xs font-black text-teal-400 uppercase tracking-wider">
                  E-posta
                </th>
                <th className="px-5 py-4 text-left text-xs font-black text-teal-400 uppercase tracking-wider">
                  Kullanım Tipi
                </th>
                <th className="px-5 py-4 text-left text-xs font-black text-teal-400 uppercase tracking-wider">
                  Promil
                </th>
                <th className="px-5 py-4 text-left text-xs font-black text-teal-400 uppercase tracking-wider">
                  Kanal
                </th>
                <th className="px-5 py-4 text-center text-xs font-black text-teal-400 uppercase tracking-wider">
                  KVKK
                </th>
                <th className="px-5 py-4 text-center text-xs font-black text-teal-400 uppercase tracking-wider">
                  Pazarlama
                </th>
                <th className="px-5 py-4 text-left text-xs font-black text-teal-400 uppercase tracking-wider">
                  Tarih
                </th>
                <th className="px-5 py-4 text-center text-xs font-black text-teal-400 uppercase tracking-wider">
                  İşlem
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-teal-500/10">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-5 py-10 text-center text-gray-400 text-lg">
                    Henüz lead bulunmamaktadır.
                  </td>
                </tr>
              ) : (
                leads.map((lead, index) => (
                  <tr key={lead.id || index} className="hover:bg-teal-500/5 smooth-transition">
                    <td className="px-5 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="w-5 h-5 rounded border-teal-500/50 bg-gray-900/50 checked:bg-teal-500 cursor-pointer"
                        data-testid={`select-lead-${lead.id}`}
                      />
                    </td>
                    <td className="px-5 py-4 text-sm text-white font-semibold">{lead.name}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">{lead.email}</td>
                    <td className="px-5 py-4 text-sm text-gray-300">
                      {lead.usage_type}
                      {lead.other_usage && (
                        <span className="text-xs text-gray-500">
                          {" "}({lead.other_usage})
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-xl text-xs font-black ${
                          lead.calculation_result
                            ? getBacColor(lead.calculation_result.current_bac)
                            : "text-gray-400 bg-gray-800/50"
                        }`}
                      >
                        {lead.calculation_result
                          ? `${lead.calculation_result.current_bac}‰`
                          : "N/A"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-block px-3 py-1.5 rounded-xl text-xs font-black bg-cyan-500/20 text-cyan-400 capitalize border border-cyan-500/30">
                        {lead.channel}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      {lead.kvkk_consent ? (
                        <span className="text-green-400 font-black text-lg">✓</span>
                      ) : (
                        <span className="text-red-400 font-black text-lg">✗</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-center">
                      {lead.marketing_consent ? (
                        <span className="text-green-400 font-black text-lg">✓</span>
                      ) : (
                        <span className="text-red-400 font-black text-lg">✗</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-400 font-medium">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="px-5 py-4 text-center">
                      <Button
                        onClick={() => handleDeleteLead(lead.id)}
                        disabled={deletingId === lead.id}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                        data-testid={`delete-lead-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      {leads.length > 0 && (
        <div className="text-sm text-teal-400 text-center font-semibold bg-gray-900/30 py-3 rounded-xl border border-teal-500/20">
          Toplam {leads.length} lead gösteriliyor
        </div>
      )}
    </div>
  );
};

export default AdminLeadTable;